<script lang="ts">
    
    import { onDestroy } from "svelte";
    import type { Game } from "../../Model/Game";
    import { State } from "../../Model/State";
    import { SubscriptionsManager } from "../../Model/SubscriptionsManager";
    import GameCard from "../Game/GameCard.svelte";
    import AspectKeeper from "../Shared/AspectKeeper.svelte";
    import Watch from "../Watch.svelte";
    import type { Cell } from "./types";

    export let game: Game;

    const numCells = 24;
    const cells: Cell[] = Array.from({ length: numCells }).map((...[, index]) => ({
        number: index + 1,
        hasComeOut: new State(false),
        isLastOne: new State(false),
    }));

    const gameSubscriptions = new SubscriptionsManager();

    $: setupGame(game);

    onDestroy(() => gameSubscriptions.unsubscribeAll());

    function setupGame(game: Game): void {
        gameSubscriptions.unsubscribeAll();

        if (!game) {
            cells.forEach(({ hasComeOut, isLastOne }) => {
                hasComeOut.set(false);
                isLastOne.set(false);
            });
            return;
        }

        gameSubscriptions.subscribe(game.numbers, numbers => {
            const [lastNumber] = numbers.slice(-1);
            cells.forEach(cell => {
                cell.hasComeOut.set(numbers.includes(cell.number));
                cell.isLastOne.set(lastNumber === cell.number);
            });
        });
    }

</script>

<AspectKeeper ratio={1.065}>
    <div class="numbers-grid notranslate">
        {#each cells as { hasComeOut, isLastOne, number }}
            <Watch state={[hasComeOut, isLastOne]} let:value={[hasComeOut, isLastOne]}>
                <div class="cell" class:has-come-out={hasComeOut} class:is-last-one={isLastOne}>
                    <GameCard {number} gameType={game.type} />
                </div>
            </Watch>
        {/each}
    </div>
</AspectKeeper>

<style lang="scss">

    .numbers-grid {
        width: 100%;
        height: 100%;
        display: grid;
        grid-template-columns: repeat(6, 1fr);
        grid-template-rows: repeat(4, 1fr);
        gap: 0.5rem;
        perspective: 500px;
    }

    .cell {
        display: flex;
        justify-content: center;
        align-items: center;
        opacity: 0.25;
        transition-duration: 0.4s;
        transform: scale(0);

        &.has-come-out {
            opacity: 1;
        }

        @for $i from 1 through 54 {
            &:nth-child(#{$i}) {
                animation: cell 0.3s cubic-bezier(.44,.15,.69,1.58) #{0.01s * $i} forwards;
            }
        }
    }

    @keyframes cell {
        0% {
            transform: scale(0);
        }
        100% {
            transform: scale(1);
        }
    }

</style>

